import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { scenariosApi } from '../../api/scenarios';

export default function EditScenarioDialog({ 
  open, 
  onClose, 
  onSuccess, 
  scenario,
  partNumber,
  programId
}) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    assumptions: '',
    key_differences: ''
  });
  const [error, setError] = useState('');

  useEffect(() => {
    if (scenario) {
      setFormData({
        name: scenario.name || '',
        description: scenario.description || '',
        assumptions: scenario.assumptions || '',
        key_differences: scenario.key_differences || ''
      });
    }
  }, [scenario]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await scenariosApi.updateScenario(partNumber, scenario.id, formData, { programId });

      if (response.success) {
        onSuccess(response.data);
        onClose();
      } else {
        setError(response.error || 'Failed to update scenario');
      }
    } catch (err) {
      setError(err.message || 'Failed to update scenario');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit Scenario</DialogTitle>
          <DialogDescription>
            Update scenario details and assumptions
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Scenario Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Scenario Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Mexico Alternative"
              required
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description of this scenario..."
              rows={2}
            />
          </div>

          {/* Assumptions */}
          <div className="space-y-2">
            <Label htmlFor="assumptions">Assumptions</Label>
            <Textarea
              id="assumptions"
              value={formData.assumptions}
              onChange={(e) => setFormData({ ...formData, assumptions: e.target.value })}
              placeholder="Key assumptions for this scenario..."
              rows={3}
            />
          </div>

          {/* Key Differences */}
          <div className="space-y-2">
            <Label htmlFor="key_differences">Key Differences</Label>
            <Textarea
              id="key_differences"
              value={formData.key_differences}
              onChange={(e) => setFormData({ ...formData, key_differences: e.target.value })}
              placeholder="How does this differ from other scenarios..."
              rows={3}
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button 
              type="button" 
              variant="outline" 
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading || !formData.name}>
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
